/* eslint-disable no-await-in-loop */
// Google My Business integration service
// Wraps backend endpoints for auth flow and data retrieval

import { callApi } from 'src/utils/apiCall';

function buildQuery(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export async function gmbGetAuthUrl(practiceId) {
  const resp = await callApi(`/api/v1/practices/${practiceId}/googlemybusiness/auth_url/`);
  if (resp.status === 200 && resp?.data?.auth_url) return resp.data.auth_url;
  throw new Error(resp?.data?.message || 'Failed to get Google auth URL');
}

export async function gmbCallback(practiceId, code) {
  const body = { code };
  const resp = await callApi(`/api/v1/practices/${practiceId}/googlemybusiness/callback/`, 'post', body);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Google authorization failed');
}

export async function gmbListAccounts(practiceId) {
  const resp = await callApi(`/api/v1/practices/${practiceId}/googlemybusiness/`);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to load Google accounts');
}

// Disconnect / remove a Google Business account from a practice
export async function gmbDeleteAccount(practiceId, accountPk) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/`;
  const resp = await callApi(uri, 'delete');
  if (resp.status === 200 || resp.status === 204) return true;
  throw new Error(resp?.data?.message || 'Failed to disconnect Google account');
}

export async function gmbListReviews(practiceId, accountId, locationId, params = {}) {
  const qs = buildQuery(params);
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountId}/locations/${locationId}/reviews/${qs}`;
  const resp = await callApi(uri);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to fetch reviews');
}

export async function gmbGetReviewStats(practiceId, accountId, locationId, params = {}) {
  const qs = buildQuery(params);
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountId}/locations/${locationId}/review_stats/${qs}`;
  const resp = await callApi(uri);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to fetch review stats');
}

// List GBP locations for a given Google account
export async function gmbListLocations(practiceId, accountId, params = {}) {
  const qs = buildQuery(params);
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountId}/locations/${qs}`;
  const resp = await callApi(uri);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to load Google locations');
}

// Fetch GBP locations directly from Google (not yet added to the practice)
export async function gmbGetAccountLocations(practiceId, accountId, params = {}) {
  const qs = buildQuery(params);
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountId}/get_locations/${qs}`;
  const resp = await callApi(uri);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to load Google locations');
}

// Add a Google location to a practice
export async function gmbAddLocation(practiceId, accountId, body) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountId}/locations/`;
  const resp = await callApi(uri, 'post', body);
  if (resp.status === 200 || resp.status === 201) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to add location to practice');
}

// Remove a Google location from a practice
export async function gmbRemoveLocation(practiceId, accountId, locationPk) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountId}/locations/${locationPk}/`;
  const resp = await callApi(uri, 'delete');
  if (resp.status === 200 || resp.status === 204) return true;
  throw new Error(resp?.data?.message || 'Failed to remove location from practice');
}

// Trigger backend to sync locations for a given Google account (by account PK)
export async function gmbSyncLocations(practiceId, accountPk) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/sync_locations/`;
  const resp = await callApi(uri, 'post');
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to sync locations');
}

// Trigger backend to sync reviews for a given location (by location DB id)
// Includes retry logic since POST requests are not automatically retried by callApi
export async function gmbSyncReviews(practiceId, accountPk, locationPk, options = {}) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/sync_reviews/`;
  const maxRetries = options.maxRetries ?? 3;
  const baseDelay = options.baseDelay ?? 1000;

  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const resp = await callApi(uri, 'post');

      // Success
      if (resp.status === 200) return resp.data;

      // Non-retriable errors (4xx except 429)
      if (resp.status >= 400 && resp.status < 500 && resp.status !== 429) {
        throw new Error(resp?.data?.message || 'Failed to sync reviews');
      }

      // Retriable errors: 429, 5xx, network errors
      lastError = new Error(resp?.data?.message || `Sync failed with status ${resp.status}`);

      // Don't retry on last attempt
      if (attempt < maxRetries) {
        // Exponential backoff with jitter
        const delay = baseDelay * (2 ** attempt) + Math.random() * 500;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    } catch (err) {
      lastError = err;

      // Check if request was cancelled (AbortError)
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        throw err; // Don't retry cancelled requests
      }

      // Don't retry on last attempt
      if (attempt < maxRetries) {
        const delay = baseDelay * (2 ** attempt) + Math.random() * 500;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Failed to sync reviews after retries');
}

// Trigger backend to sync posts for a given location (by location DB id)
export async function gmbSyncPosts(practiceId, accountPk, locationPk) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/sync_posts/`;
  const resp = await callApi(uri, 'post');
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to sync posts');
}

// Reply to a review
export async function gmbReplyReview(practiceId, accountPk, locationPk, reviewPk, replyText) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/reviews/${reviewPk}/reply/`;
  const body = { comment: replyText };
  const resp = await callApi(uri, 'post', body);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to reply to review');
}

// Delete a review (from our system record)
export async function gmbDeleteReview(practiceId, accountPk, locationPk, reviewPk) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/reviews/${reviewPk}/`;
  const resp = await callApi(uri, 'delete');
  if (resp.status === 200 || resp.status === 204) return true;
  throw new Error(resp?.data?.message || 'Failed to delete review');
}

export async function gmbPatchReview(practiceId, accountPk, locationPk, reviewPk, payload = {}) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/reviews/${reviewPk}/`;
  const resp = await callApi(uri, 'patch', payload);
  if (resp.status === 200 || resp.status === 202) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to update review');
}

export async function gmbListPosts(practiceId, accountPk, locationPk, params = {}) {
  const qs = buildQuery(params);
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/posts/${qs}`;
  const resp = await callApi(uri);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to fetch posts');
}

export async function gmbCreatePost(practiceId, accountPk, locationPk, body) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/posts/create_post/`;
  const resp = await callApi(uri, 'post', body);
  if (resp.status === 200 || resp.status === 201) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to create post');
}

export async function gmbUpdatePost(practiceId, accountPk, locationPk, postId, body) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/posts/${postId}/update_post/`;
  const resp = await callApi(uri, 'put', body);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to update post');
}

export async function gmbDeletePost(practiceId, accountPk, locationPk, postId) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountPk}/locations/${locationPk}/posts/${postId}/delete_post/`;
  const resp = await callApi(uri, 'delete');
  if (resp.status === 200 || resp.status === 204) return true;
  throw new Error(resp?.data?.message || 'Failed to delete post');
}

// Update one or more GBP location fields via a single PATCH.
// fields: partial location object, e.g. { regular_hours, special_hours }
export async function gmbUpdateLocationFields(practiceId, accountId, locationPk, fields) {
  const uri = `/api/v1/practices/${practiceId}/googlemybusiness/${accountId}/locations/${locationPk}/`;
  const resp = await callApi(uri, 'patch', fields);
  if (resp.status === 200) return resp.data;
  throw new Error(resp?.data?.message || 'Failed to update location');
}
